import { Box, Button, Icon, Typography } from "@outschool/backpack";
import { REFER_CREDIT_CENTS } from "@outschool/business-rules";
import { ParentProfileQuery } from "@outschool/gql-frontend-generated";
import { faScreenUsers, faStar, faUser } from "@outschool/icons";
import { renderCurrencyString, useTranslation } from "@outschool/localization";
import { formatDate } from "@outschool/time";
import { useSession } from "@outschool/ui-auth";
import {
  TrackedButton,
  UserHeadshotImage,
} from "@outschool/ui-components-shared";
import { CollapsedText } from "@outschool/ui-legacy-component-library";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import * as Routes from "../../../../shared/Routes";
import ReferralsModal from "../../ReferralsModal";
import { ShareProfileModal } from "./ShareProfileModal";
import { ShareProfileWithReferralModal } from "./ShareProfileWithReferralModal";

type Props = {
  parent: NonNullable<
    NonNullable<ParentProfileQuery["publicProfile"]>["parent"]
  >;
  isCurrentUsersProfile: boolean;
  privateLink: string;
};

export function ParentProfileCard({
  parent,
  isCurrentUsersProfile,
  privateLink,
}: Props) {
  const { currentUser } = useSession();
  const [showProfileWithReferralModal, setShowProfileWithReferralModal] =
    React.useState(false);
  const [showReferralsModal, setShowReferralsModal] = React.useState(false);
  const [showShareProfileModal, setShowShareProfileModal] =
    React.useState(false);
  const { t } = useTranslation(
    "client\\routes\\ParentProfile\\ParentProfileCard",
  );
  const Link = useLinkComponent();

  const isEducator =
    !!currentUser?.teacher_approved_at && !currentUser?.suspended_at;

  const creditCashAmount = renderCurrencyString({
    priceInCents: REFER_CREDIT_CENTS,
    showCurrencyCode: false,
  });

  const LinkComponent = ({ children }: $TSFixMe) =>
    isCurrentUsersProfile ? (
      <Link to={Routes.parentSettingsPath()}>{children}</Link>
    ) : (
      <>{children}</>
    );

  return (
    <>
      <Box>
        {/* Profile Picture & Name / Edit Profile */}
        <Box flex sx={{ alignItems: "center" }}>
          <UserHeadshotImage user={parent} size={80} />
          <Box sx={{ marginLeft: "2rem", overflowWrap: "anywhere" }}>
            <Typography className="amp-block" variant="h5" emphasized>
              {parent.publicName}
            </Typography>

            <LinkComponent>
              {isCurrentUsersProfile && (
                <Button variant="link" aria-label={t("Edit profile")}>
                  <Typography>{t("Edit Profile")}</Typography>
                </Button>
              )}
            </LinkComponent>
          </Box>
        </Box>

        {/* Profile Text */}
        <Box sx={{ mb: 12 }}>
          <CollapsedText
            allowCollapse={true}
            maxCharacters={200}
            sx={theme => ({
              fontSize: "1.4rem",
              color: theme.palette.neutral[700],
            })}
            buttonSx={{
              fontSize: "1.4rem",
            }}
          >
            {parent.introduction
              ? parent.introduction
              : t(
                  "{{parentName}} is on Outschool to discover our small-group online classes for kids. Join us!",
                  { parentName: parent.publicName },
                )}
          </CollapsedText>
        </Box>

        {/* Outschooler Stats */}
        <Box>
          <Box
            flex
            sx={{
              justifyContent: "space-between",
              fontSize: "1.4rem",
              mb: "0.75rem",
            }}
          >
            <Box flex sx={{ alignItems: "center", color: "grey.600" }}>
              <Icon
                icon={faUser}
                size="small"
                sx={{ marginRight: "0.75rem" }}
              />
              {t("Outschooler Since")}
            </Box>
            <Typography emphasized>{formatDate(parent.created_at)}</Typography>
          </Box>

          <Box
            flex
            sx={{
              justifyContent: "space-between",
              fontSize: "1.4rem",
              mb: "0.75rem",
            }}
          >
            <Box flex sx={{ alignItems: "center", color: "grey.600" }}>
              <Icon
                icon={faScreenUsers}
                size="small"
                sx={{ marginRight: "0.75rem" }}
              />
              {t("Classes Taken")}
            </Box>
            <Typography emphasized>{parent.uniqueActivityCount}</Typography>
          </Box>

          <Box
            flex
            sx={{
              justifyContent: "space-between",
              fontSize: "1.4rem",
              mb: "0.75rem",
            }}
          >
            <Box flex sx={{ alignItmes: "center", color: "grey.600" }}>
              <Icon
                icon={faStar}
                size="small"
                sx={{ marginRight: "0.75rem" }}
              />
              {t("Reviews Written")}
            </Box>
            <Typography emphasized>{parent.reviewCount}</Typography>
          </Box>
        </Box>

        {isCurrentUsersProfile && !isEducator && (
          <>
            {/* G20G20 Referral */}
            <TrackedButton
              trackingName="share profile with refer a friend"
              trackingUniqueId={parent.uid}
              disabled={!!(currentUser?.privacy_state === "only me")}
              onClick={() => setShowProfileWithReferralModal(true)}
              variant="contained"
              sx={{ width: "100%", my: 12 }}
            >
              {t("Refer a Friend & Earn {{creditCashAmount}}!", {
                creditCashAmount,
              })}
            </TrackedButton>
            <Box sx={{ px: "1.2rem", py: "0.75rem" }}>
              <Typography sx={{ color: "grey.600", fontSize: "1.4rem" }}>
                {t(
                  "Share your profile link with friends. They’ll get {{creditCashAmount}} off their first class, and you’ll earn {{creditCashAmount}} when they join. Everyone wins!",
                  {
                    creditCashAmount,
                  },
                )}
              </Typography>
            </Box>

            {/* Referral Status */}
            <Box
              flex
              sx={{
                w: "100%",
                justifyContent: "center",
                pt: "0.75rem",
                pb: "0.5rem",
              }}
            >
              <TrackedButton
                trackingName="referral status"
                trackingUniqueId={parent?.uid}
                variant="link"
                color="inherit"
                onClick={() => setShowReferralsModal(true)}
              >
                {t("Referral Status")}
              </TrackedButton>
            </Box>
          </>
        )}

        {/* Modals */}
        <ShareProfileWithReferralModal
          open={showProfileWithReferralModal}
          onClose={() => setShowProfileWithReferralModal(false)}
          url={Routes.parentProfileWithLinkUidJoinUrl(
            isCurrentUsersProfile ? currentUser : parent,
            { utm_campaign: "share_parent_link" },
          )}
          privateLink={privateLink}
          referralCode={parent.slug_id}
        />
        <ReferralsModal
          open={showReferralsModal}
          onClose={() => setShowReferralsModal(false)}
          isEducator={isEducator}
        />
        <ShareProfileModal
          open={showShareProfileModal}
          onClose={() => setShowShareProfileModal(false)}
          url={Routes.publicParentProfileUrl(
            isCurrentUsersProfile ? currentUser : parent,
          )}
        />
      </Box>
    </>
  );
}
